import React, { useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle,
    Archive,
    CheckCircle2,
    Download,
    FileText,
    Loader2,
    RefreshCw,
    X,
} from 'lucide-react';
import type { Customer, Invoice, InvoiceDatevConfig, InvoiceStatus } from '../../types';
import { Button } from '../Button';
import { fetchDeductionSources, fetchInvoices } from '../../services/invoiceService';
import { isExternalAdvanceId } from '../../services/externalAdvances';
import {
    bookingRowCount,
    buildBelegZipEntries,
    buildExtfBuchungsstapel,
    formatYearMonthDay,
    UNVERIFIED_DATEV_FIELDS,
} from '../../services/datev/datevExport';
import type { DatevDocumentEntry } from '../../services/datev/belegArchive';
import { buildDocumentXml, documentGuidForInvoice } from '../../services/datev/belegArchive';
import { toCp1252Bytes } from '../../services/datev/cp1252';
import { exportCustomersDatevExtf } from '../../services/customerListExport';
import type { GobdArchiveResult } from '../../services/gobdArchive';
import { buildGobdArchive, fetchArchivedPdfBytes } from '../../services/gobdArchive';
import { formatCents } from '../../services/money';
import { InvoiceStatusBadge } from './InvoiceStatusBadge';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';

/**
 * Export nach DATEV bzw. Simba und Erzeugung des GoBD-Belegarchivs.
 *
 * Der Dialog schreibt NICHTS zurueck. Er baut die Dateien, laedt sie herunter
 * und meldet die exportierten Belege ueber onExported nach oben — das Setzen von
 * exportedAt gehoert in den Service, damit es genau einmal und an einer Stelle
 * passiert.
 *
 * Drei Dinge sind hier bewusst unbequem geloest:
 *
 * 1. Der Hinweis auf die ungepruefte Feldbelegung (UNVERIFIED_DATEV_FIELDS)
 *    steht ganz oben, ist nicht einklappbar und muss bestaetigt werden. Ein
 *    falsch belegtes Feld faellt in der Buchhaltung erst beim Kontenabgleich
 *    auf, und dann sucht jemand lange.
 * 2. Entwuerfe im Zeitraum werden mit Begruendung aufgelistet, nicht still
 *    weggelassen. Ein stiller Sprung waere in der Buchhaltung erst als Luecke
 *    im Nummernkreis aufgefallen.
 * 3. Bereits exportierte Belege sind markiert und brauchen eine zweite,
 *    getrennte Bestaetigung — ein zweiter Import derselben Buchungen verdoppelt
 *    den Umsatz.
 *
 * Die Belege holt der Dialog SELBST und vollstaendig, statt sie sich reichen zu
 * lassen. Die Belegliste laedt ihre Belege ohne Positionen; ein von dort
 * durchgereichter Beleg traegt `lines: []`, und der Buchungstext verloere still
 * die Projektnummern aller Belege ohne eingefrorenen Projektvermerk
 * (Altbestand). Still heisst: keine Fehlermeldung, kein leeres Feld — nur ein
 * Text, in dem die Nummer fehlt, und das faellt beim Steuerberater niemandem
 * auf.
 */

/**
 * Status vor der Ausstellung.
 *
 * Spiegelt PRE_ISSUE_STATUSES aus datevExport.ts, das dort nicht exportiert ist.
 * Ein Auseinanderlaufen bleibt harmlos: der Exporter wirft bei einem
 * nicht ausgestellten Beleg, statt ihn zu ueberspringen — der Fehler waere also
 * laut und nicht still.
 */
const PRE_ISSUE_STATUSES: readonly InvoiceStatus[] = [
    'DRAFT',
    'READY_FOR_REVIEW',
    'APPROVED',
    'ISSUING',
];

type BusyKind = 'csv' | 'zip' | 'gobd';

/**
 * Ergebnis des Exportvermerks beim Aufrufer.
 *
 * Der Dialog bleibt nach dem Download offen. Ohne diese Rueckmeldung wuerde er
 * Erfolg zeigen, obwohl exportedAt nicht gesetzt werden konnte — beim zweiten
 * Export desselben Zeitraums fehlt dann die Warnung und der Umsatz wird in der
 * Buchhaltung doppelt gebucht.
 */
export interface DatevExportMarkResult {
    /** true nur, wenn der Vermerk an ALLEN Belegen gesetzt wurde. */
    ok: boolean;
    /** Klartext fuer die Warnung im Dialog, wenn ok false ist. */
    message?: string;
    /**
     * Die Belege, an denen der Vermerk TATSAECHLICH gesetzt wurde.
     *
     * Auch — und gerade — im Teilfehlerfall zu fuellen: der Dialog laedt nicht
     * neu und zeigt sonst bereits vermerkte Belege weiter als "neu". Beim
     * zweiten Export desselben Zeitraums bliebe die Warnung dann aus, obwohl
     * sie am Beleg schon stuende, und der Umsatz wuerde doppelt gebucht.
     *
     * Fehlt die Angabe, gelten bei ok alle gemeldeten Belege als vermerkt (der
     * bisherige Stand) und sonst keiner.
     */
    markedIds?: string[];
    /**
     * Welche Belege vermerkt sind, ist NICHT bekannt.
     *
     * Fuer den unerwarteten Wurf des Aufrufers — etwa ein Netzabbruch, nachdem
     * ein Block schon geschrieben war. markedIds ist dann leer, aber das heisst
     * hier ausdruecklich nicht "keiner": in der Datenbank koennen Belege bereits
     * als exportiert stehen. Der Dialog darf seinen eigenen Stand danach nicht
     * mehr fuer bare Muenze nehmen, sonst warnt er beim zweiten Export desselben
     * Zeitraums nicht und der Umsatz wird doppelt gebucht.
     */
    markStateUnknown?: boolean;
}

export interface DatevExportDialogProps {
    /** Ohne Konfiguration ist kein Export moeglich; der Dialog sagt das dann. */
    datevConfig?: InvoiceDatevConfig;
    /** Kuerzel oder Name des ausloesenden Benutzers, wandert in die Kopfzeile. */
    exportedBy?: string;
    /** Vorbelegung des Zeitraums (jjjj-mm-tt). Standard ist der laufende Monat. */
    initialFrom?: string;
    initialTo?: string;
    onClose: () => void;
    /**
     * Nach einem erfolgreichen Download: die IDs der tatsaechlich exportierten
     * Belege. exportedAt wird hier NICHT gesetzt — das macht der Aufrufer und
     * meldet zurueck, ob der Vermerk vollstaendig gesetzt werden konnte.
     */
    onExported: (
        invoiceIds: string[],
        details: { kind: 'csv' | 'zip'; fileName: string; exportedAt: string },
    ) => Promise<DatevExportMarkResult>;
}

const pad = (value: number): string => String(value).padStart(2, '0');
const isoDay = (date: Date): string =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const startOfCurrentMonth = (): string => {
    const now = new Date();
    return isoDay(new Date(now.getFullYear(), now.getMonth(), 1));
};
const endOfCurrentMonth = (): string => {
    const now = new Date();
    return isoDay(new Date(now.getFullYear(), now.getMonth() + 1, 0));
};

/** Grund, warum ein Beleg im Zeitraum liegt und trotzdem nicht exportiert wird. */
const blockingReason = (invoice: Invoice): string | null => {
    if (PRE_ISSUE_STATUSES.includes(invoice.status)) {
        return 'Noch nicht ausgestellt. Ein Entwurf hat keine Rechnungsnummer und ist jederzeit änderbar — eine Buchung darauf wäre nicht nachvollziehbar.';
    }
    if (!invoice.invoiceNumber) {
        return 'Ohne Rechnungsnummer. Der Beleg lässt sich in der Buchhaltung keinem Vorgang zuordnen.';
    }
    return null;
};

/** Rechtefehler der Datenbank (Nr. 151) im Klartext, sonst wie bisher. */
const errorText = (error: unknown, fallback: string): string =>
    rechtFehlerOder(error, () => (error instanceof Error ? error.message : fallback));

/**
 * Die ids der Belege, die in dieser Auswahl ABGEZOGEN werden.
 *
 * Der Buchungsstapel braucht zu jedem Abzug die Belegart des abgezogenen
 * Belegs: ein Abschlag wird gegen das Konto der erhaltenen Anzahlungen
 * aufgeloest, eine gewoehnliche Vorrechnung mindert die Erloeszeile.
 *
 * Externe Alt-Abschlaege ('ext:…') bleiben draussen — und zwar nicht mit einer
 * geratenen Belegart, sondern gar nicht. Sie stehen in
 * public.external_advances und nicht in public.invoices; ihre id ist keine
 * uuid, und in einem `in`-Filter auf invoices.id brauechte die Datenbank sie
 * nicht erst zu suchen: sie bricht die GANZE Abfrage mit einem
 * Konvertierungsfehler ab, und damit den Export. Der Exporter erkennt sie am
 * Praefix und bucht sie nach der einmaligen Feststellung
 * datev.externalAdvanceBooking.
 */
const deductionSourceIds = (invoices: Invoice[]): string[] => [
    ...new Set(
        invoices.flatMap((invoice) =>
            (invoice.advanceDeductions ?? [])
                .map((deduction) => deduction.advanceInvoiceId)
                .filter((id) => !isExternalAdvanceId(id)),
        ),
    ),
];

/**
 * Nettoanteil der Abzuege eines Belegs.
 *
 * Kommt aus den Abzugszeilen, nicht aus den Summen: InvoiceTotals fuehrt den
 * Abzug nur BRUTTO (prepaidCents) — er mindert ja allein den Zahlbetrag,
 * waehrend Netto und Steuer der Gesamtleistung auf dem Beleg stehen bleiben
 * (§14 UStG). Fuer die Vorschau eines Buchungsstapels ist trotzdem der
 * Zuwachs die richtige Zahl, und der ist netto nur so zu bekommen.
 */
const deductedNetOf = (invoice: Invoice): number =>
    (invoice.advanceDeductions ?? []).reduce((sum, deduction) => sum + deduction.netCents, 0);

/**
 * Die Kunden fuer den Debitorenstamm des Pakets.
 *
 * Quelle sind die EINGEFRORENEN Schnappschuesse der exportierten Belege, nicht
 * der heutige Kundenstamm aus der Datenbank. Das ist die einzige Quelle, die
 * die geforderte Zusage haelt: jedes Konto, das im Buchungsstapel vorkommt,
 * steht im Stamm. Der Stapel bucht auf customerSnapshot.customerNumber — ist
 * ein Kunde seither umnummeriert, archiviert oder geloescht worden, kennt der
 * heutige Stamm dieses Konto nicht mehr, und die Kanzlei bekaeme zu genau
 * diesen Buchungen keine Beschriftung. Nebenbei spart es einen Netzaufruf und
 * beantwortet die Frage nach archivierten Kunden von selbst: im Stamm steht,
 * wer gebucht wird, und sonst niemand.
 *
 * Bei mehreren Belegen desselben Kontos gewinnt der spaeteste — die Belege
 * kommen nach Belegdatum aufsteigend, der letzte Schnappschuss ist der
 * aktuellste bekannte Stand.
 *
 * Belege ohne Kundennummer im Schnappschuss bleiben draussen; an ihnen bricht
 * der Buchungsstapel ohnehin ab, bevor dieser Stamm gebaut wird.
 */
const debtorCustomersOf = (invoices: Invoice[]): Customer[] => {
    const byNumber = new Map<string, Customer>();
    for (const invoice of invoices) {
        const customer = invoice.customerSnapshot;
        const number = customer?.customerNumber?.trim();
        if (!customer || !number) continue;
        byNumber.set(number, customer);
    }
    return [...byNumber.values()];
};

/** Die Bytes sind fertig kodiert und gehen unveraendert in den Blob. */
const download = async (bytes: Uint8Array, fileName: string, mimeType: string): Promise<void> => {
    const { saveAs } = await import('file-saver');
    saveAs(new Blob([bytes as BlobPart], { type: mimeType }), fileName);
};

export const DatevExportDialog: React.FC<DatevExportDialogProps> = ({
    datevConfig,
    exportedBy,
    initialFrom,
    initialTo,
    onClose,
    onExported,
}) => {
    const [from, setFrom] = useState(initialFrom ?? startOfCurrentMonth());
    const [to, setTo] = useState(initialTo ?? endOfCurrentMonth());
    const [formatAcknowledged, setFormatAcknowledged] = useState(false);
    const [reExportAcknowledged, setReExportAcknowledged] = useState(false);
    const [busy, setBusy] = useState<BusyKind | null>(null);
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [gobd, setGobd] = useState<GobdArchiveResult | null>(null);
    /** null heisst "noch nicht da" — davon haengt ab, ob ueberhaupt etwas zaehlbar ist. */
    const [invoices, setInvoices] = useState<Invoice[] | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    /** Hochzaehlen loest einen neuen Ladeversuch aus; sonst nichts. */
    const [loadAttempt, setLoadAttempt] = useState(0);
    /**
     * Der letzte Exportvermerk ist unbekannt ausgegangen (markStateUnknown).
     *
     * Solange das gilt, ist der angezeigte Exportstand nicht belastbar: der
     * Dialog holt ihn deshalb neu aus der Datenbank UND verlangt fuer jeden
     * weiteren Export dieses Dialogs die getrennte Bestaetigung — auch dann,
     * wenn danach kein Beleg als exportiert gilt. Denn die Datei ist beim
     * Anwender gelandet; ist der Vermerk gar nicht geschrieben worden, wuerde
     * ein zweiter Stapel ohne jede Warnung durchlaufen.
     */
    const [markStateUnknown, setMarkStateUnknown] = useState(false);

    /**
     * Die Belege MIT Positionen holen.
     *
     * Das Dep-Array nennt bewusst nur den Versuchszaehler — insbesondere keinen
     * Ladezustand, den der Effekt selbst setzt: der Effekt raeumte sich dann
     * ueber seine eigene Aufraeumfunktion ab und verwuerfe sein eigenes
     * Ergebnis.
     */
    useEffect(() => {
        let cancelled = false;
        setInvoices(null);
        setLoadError(null);
        void (async () => {
            try {
                const full = await fetchInvoices();
                if (!cancelled) setInvoices(full);
            } catch (e) {
                if (!cancelled)
                    setLoadError(
                        errorText(
                            e,
                            'Die Belege konnten nicht geladen werden. Ohne sie lässt sich kein Buchungsstapel erzeugen.',
                        ),
                    );
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [loadAttempt]);

    const preview = useMemo(() => {
        // Zeichenweiser Vergleich reicht: alle Belegdaten sind jjjj-mm-tt.
        const inPeriod = (invoices ?? [])
            .filter((invoice) => invoice.invoiceDate >= from && invoice.invoiceDate <= to)
            .sort((a, b) => a.invoiceDate.localeCompare(b.invoiceDate));

        const exportable: Invoice[] = [];
        const blocked: Array<{ invoice: Invoice; reason: string }> = [];
        for (const invoice of inPeriod) {
            const reason = blockingReason(invoice);
            if (reason) blocked.push({ invoice, reason });
            else exportable.push(invoice);
        }

        const alreadyExported = exportable.filter((invoice) => invoice.exportedAt);

        // Gezeigt wird die tatsächlich GEFORDERTE Größe, nicht die
        // Gesamtleistung. totals.netTotalCents/grossTotalCents sind der Stand
        // VOR dem Abzug — auf dem Beleg richtig (§14 UStG weist die
        // Gesamtleistung aus), als Vorschau eines Buchungsstapels falsch:
        // gebucht wird der Zuwachs. Wer hier die Gesamtleistung sah, sah
        // dieselbe überhöhte Zahl wie im Export und hatte gegen sie keine
        // Gegenprobe. Genau daran ist der Doppelbuchungs-Fehler so lange
        // unentdeckt geblieben.
        const totalNetCents = exportable.reduce((sum, i) => sum + i.totals.netTotalCents, 0);
        const totalGrossCents = exportable.reduce((sum, i) => sum + i.totals.grossTotalCents, 0);
        const deductedNetCents = exportable.reduce((sum, i) => sum + deductedNetOf(i), 0);
        const deductedGrossCents = exportable.reduce((sum, i) => sum + i.totals.prepaidCents, 0);
        const netCents = totalNetCents - deductedNetCents;
        const grossCents = exportable.reduce((sum, i) => sum + i.totals.payableCents, 0);
        // Nicht > 0: kumulativ kann ein einzelner Abzug eine Gutschrift sein,
        // die Summe also auch negativ ausfallen. Auch dann sind Abzüge im
        // Spiel und gehören ausgewiesen.
        const hasDeductions = deductedGrossCents !== 0 || deductedNetCents !== 0;

        // Zeilenzahl. Je Steuersatz und LEISTUNGSMONAT eine Erlöszeile und je
        // Steuersatz mit Abschlagsabzug eine zusätzliche Verrechnungszeile.
        //
        // Gezählt wird im Exporter selbst (bookingRowCount), nicht hier: seit
        // der Aufteilung in Monatszeilen hängt die Zahl an der Zuordnung der
        // Positionen, und die Verteilregel hier ein zweites Mal aufzuschreiben
        // hieße, zwei Fassungen derselben Regel zu pflegen — die Vorschau wäre
        // dann still falsch statt sichtbar unscharf. Der Dialog lädt die
        // Belege ohnehin vollständig, die Zuordnung liegt ihm also vor.
        //
        // Unscharf bleibt allein der Abzug: ob er aus einer Abschlagsrechnung
        // stammt, hängt an der Belegart des abgezogenen Belegs und steht erst
        // nach einer Datenbankabfrage fest. Deshalb dann eine OBERGRENZE, die
        // als solche benannt wird. Ohne Abzüge ist die Zahl exakt.
        const rowCounts = exportable.map((invoice) => bookingRowCount(invoice));
        const bookingRows = rowCounts.reduce((sum, count) => sum + count.rows, 0);
        const bookingRowsExact = rowCounts.every((count) => count.exact);

        // Summen je Belegmonat: das Belegdatum bestimmt die Buchungsperiode
        // beim Steuerberater. Erst ab zwei Monaten angezeigt — bei einem
        // Monat stünde nur die Gesamtsumme ein zweites Mal da.
        const byMonth = new Map<
            string,
            { count: number; netCents: number; grossCents: number; deductedGrossCents: number }
        >();
        for (const invoice of exportable) {
            const key = invoice.invoiceDate.slice(0, 7);
            const bucket = byMonth.get(key) ?? {
                count: 0,
                netCents: 0,
                grossCents: 0,
                deductedGrossCents: 0,
            };
            bucket.count += 1;
            // Wie oben: das Geforderte, nicht die Gesamtleistung.
            bucket.netCents += invoice.totals.netTotalCents - deductedNetOf(invoice);
            bucket.grossCents += invoice.totals.payableCents;
            bucket.deductedGrossCents += invoice.totals.prepaidCents;
            byMonth.set(key, bucket);
        }

        // Belege, die einen EXTERNEN Alt-Abschlag abziehen.
        //
        // Deren Belegart ist nirgends hinterlegt: sie stehen in
        // public.external_advances, das nur Nummer, Datum und Betrag führt —
        // wie sie in der Buchhaltung liegen, weiß nur das Vorsystem. Der
        // Exporter erkennt sie am 'ext:'-Präfix und bucht sie nach der
        // einmaligen Feststellung datev.externalAdvanceBooking; solange die
        // fehlt, bricht er ab, statt zu raten. Die Liste steht deshalb hier
        // und nicht erst in der Fehlermeldung nach dem Klick.
        const withExternalDeductions = exportable
            .map((invoice) => ({
                invoice,
                sources: (invoice.advanceDeductions ?? [])
                    .filter((deduction) => isExternalAdvanceId(deduction.advanceInvoiceId))
                    .map((deduction) => deduction.invoiceNumber || deduction.advanceInvoiceId),
            }))
            .filter((entry) => entry.sources.length > 0);

        const monthly = [...byMonth.entries()]
            .map(([month, sums]) => ({ month, ...sums }))
            .sort((a, b) => a.month.localeCompare(b.month));

        return {
            inPeriod,
            exportable,
            blocked,
            alreadyExported,
            netCents,
            grossCents,
            totalNetCents,
            totalGrossCents,
            deductedNetCents,
            deductedGrossCents,
            hasDeductions,
            withExternalDeductions,
            bookingRows,
            bookingRowsExact,
            monthly,
        };
    }, [invoices, from, to]);

    const periodValid = Boolean(from) && Boolean(to) && from <= to;
    const reExportBlocked =
        (preview.alreadyExported.length > 0 || markStateUnknown) && !reExportAcknowledged;
    // invoices !== null steht ausdruecklich da, obwohl exportable.length > 0 es
    // schon impliziert: der Stapel darf nur aus vollstaendig geladenen Belegen
    // entstehen, und diese Bedingung soll man hier lesen koennen.
    const canExport =
        !busy &&
        invoices !== null &&
        periodValid &&
        Boolean(datevConfig) &&
        preview.exportable.length > 0 &&
        formatAcknowledged &&
        !reExportBlocked;

    // Die Stapelbezeichnung wird bewusst NICHT mitgegeben: die Vorbelegung
    // baut buildHeaderRow selbst aus dem Zeitraum (datevBatchDescription),
    // damit sie in die 30 Zeichen des Kopfzeilenfelds passt. Ein hier
    // formatierter Text war die zweite Fassung derselben Vorbelegung und
    // wurde mitten im Wort gekappt.
    // belegLinkIds nennt die Belege, deren PDF im selben Paket liegt; nur sie
    // bekommen einen Beleglink. Der reine CSV-Export uebergibt bewusst nichts:
    // er verschickt keine Belege, und ein Verweis auf ein nicht mitgeliefertes
    // Dokument waere ein Verweis ins Leere.
    // deductionSources nennt zu jedem abgezogenen Beleg Belegart und
    // Exportvermerk. Erst damit weiss der Stapel, ob ein Abzug ein Abschlag
    // auf dem Anzahlungskonto ist (Aufloesung) oder bereits gebuchter Erloes
    // (Minderung der Erloeszeile) — ohne das bucht er den vollen Gesamtstand,
    // und der bereits gebuchte Umsatz entsteht ein zweites Mal.
    //
    // Deshalb ist das hier async. Die Abfrage steht bewusst IN buildCsv und
    // nicht in den beiden Aufrufern: CSV und ZIP bauen denselben Stapel, und
    // eine zweite Stelle hiesse, dass einer der beiden Wege sie irgendwann
    // vergisst — der Export liefe dann weiter, nur mit den falschen Konten.
    const buildCsv = async (belegLinkIds?: ReadonlySet<string>) => {
        const deductionSources = await fetchDeductionSources(
            deductionSourceIds(preview.exportable),
        );
        return buildExtfBuchungsstapel(
            preview.exportable,
            datevConfig,
            { from, to },
            { exportedBy, belegLinkIds, deductionSources },
        );
    };

    /**
     * Auf den Exportvermerk WARTEN und sein Ergebnis melden. Wurde er nicht
     * vollstaendig gesetzt, darf hier keine Erfolgsmeldung stehen — der Nutzer
     * hielte den Beleg sonst fuer markiert und exportierte ihn ein zweites Mal.
     */
    const reportExport = async (kind: 'csv' | 'zip', fileName: string) => {
        const exportedAt = new Date().toISOString();
        const ids = preview.exportable.map((invoice) => invoice.id);
        const result = await onExported(ids, { kind, fileName, exportedAt });

        // Den Vermerk im eigenen Bestand nachziehen: der Dialog bleibt offen
        // und laedt nicht neu. Ohne das zeigte er die eben exportierten Belege
        // weiter als "neu", und die Warnung vor dem zweiten Export bliebe aus,
        // obwohl sie am Beleg schon stuende.
        //
        // Ausdruecklich AUCH im Teilfehlerfall, und zwar genau fuer die
        // gemeldeten Belege: dort ist ein Teil in der Datenbank vermerkt, der
        // Rest nicht — die Anzeige muss diesen Stand abbilden, nicht den
        // bequemeren. Ohne markedIds bleibt es beim bisherigen Verhalten.
        //
        // Ist der Ausgang dagegen unbekannt, waere jedes Nachziehen geraten:
        // der eigene Stand wird verworfen und aus der Datenbank neu geholt —
        // derselbe Weg, den der Dialog beim Oeffnen geht. Bis er da ist, zeigt
        // er "wird geladen" statt Zahlen, und exportieren laesst sich in der
        // Zwischenzeit ohnehin nicht.
        if (result.markStateUnknown) {
            setMarkStateUnknown(true);
            setLoadAttempt((n) => n + 1);
        } else {
            // Der Ausgang dieses Laufs ist bekannt, der angezeigte Stand damit
            // wieder belastbar. Absichtlich erst hier und nicht schon beim
            // Start des Exports: bricht der Lauf vorher ab, bleibt die alte
            // Unsicherheit bestehen — sie ist ja nicht ausgeraeumt.
            setMarkStateUnknown(false);
            const exported = new Set(result.markedIds ?? (result.ok ? ids : []));
            if (exported.size > 0) {
                setInvoices(
                    (prev) =>
                        prev?.map((invoice) =>
                            exported.has(invoice.id) ? { ...invoice, exportedAt } : invoice,
                        ) ?? prev,
                );
            }
        }

        // Die Bestaetigung gilt fuer GENAU diesen Export. Bliebe sie stehen,
        // liefe der naechste Lauf im selben Dialog ohne die Warnung durch —
        // und die eben exportierten Belege sind jetzt selbst "schon
        // exportiert".
        setReExportAcknowledged(false);
        return result;
    };

    const handleCsv = async () => {
        setBusy('csv');
        setError(null);
        setNotice(null);
        try {
            const { fileName, bytes } = await buildCsv();
            // text/csv, nicht text/plain: die Bytes sind Windows-1252 und
            // duerfen auf dem Weg in die Datei nicht noch einmal kodiert werden.
            await download(bytes, fileName, 'text/csv');
            const marked = await reportExport('csv', fileName);
            if (marked.ok) setNotice(`${fileName} wurde heruntergeladen.`);
            else
                setError(
                    marked.message ??
                        `${fileName} wurde heruntergeladen, der Exportvermerk konnte aber nicht gesetzt werden.`,
                );
        } catch (e) {
            setError(errorText(e, 'Der Export ist fehlgeschlagen.'));
        } finally {
            setBusy(null);
        }
    };

    const handleZip = async () => {
        setBusy('zip');
        setError(null);
        setNotice(null);
        try {
            // Ein einziger Zeitpunkt fuer den ganzen Lauf. Waehrend des Packens
            // erneut abgelesen, stuende im Manifest eine andere Sekunde als im
            // Rest des Pakets — ein Unterschied ohne Aussage.
            const generatedAt = new Date();
            const { entries, missingPdf } = buildBelegZipEntries(preview.exportable);
            const byPath = new Map<string, Invoice>(
                preview.exportable
                    .filter((invoice) => invoice.pdfUrl)
                    .map((invoice) => [invoice.pdfUrl as string, invoice] as const),
            );

            const JSZip = (await import('jszip')).default;
            const zip = new JSZip();

            // Die offenen Punkte wandern als Textdatei mit ins Paket. Es geht an
            // die Kanzlei, und dort soll niemand das Format fuer geprueft halten,
            // nur weil die Datei sauber aussieht.
            zip.file(
                'PRUEFEN-vor-dem-Import.txt',
                [
                    'Offene Punkte der DATEV-Feldbelegung',
                    '====================================',
                    '',
                    'Die Feldbelegung dieses Exports wurde ohne DATEV-Formatbeschreibung',
                    'erstellt. Bitte vor dem produktiven Import prüfen:',
                    '',
                    ...UNVERIFIED_DATEV_FIELDS.map((text, index) => `${index + 1}. ${text}`),
                    '',
                ].join('\r\n'),
            );

            // Der Belegordner heisst wie im Referenzexport der Kanzlei — der
            // Zeitraum steckt im Namen, damit zwei Pakete nebeneinander liegen
            // koennen. Die Datumsteile kommen aus derselben Funktion, mit der
            // buildExtfBuchungsstapel seinen Dateinamen baut; zweimal
            // formatiert waeren es zwei Schreibweisen, die auseinander laufen.
            const belegFolderName =
                `DATEV_Rechnungsausgang_${formatYearMonthDay(from, 'Zeitraum von')}_bis_` +
                `${formatYearMonthDay(to, 'Zeitraum bis')}`;
            const folder = zip.folder(belegFolderName);

            // Was TATSAECHLICH ins ZIP gegangen ist — nicht, was geplant war.
            // Ein Beleg kann hier uebersprungen werden, und das Holen des PDF
            // kann werfen. Ein Manifest, das mehr Belege behauptet, als im
            // Ordner liegen, ist schlimmer als gar keines: DATEV sucht dann
            // eine Datei, die es nicht gibt, und die Kanzlei sucht mit.
            const written: DatevDocumentEntry[] = [];
            // Dieselben Bytes wandern in den Ordner UND in die importierbare
            // Beleg-ZIP. Gehalten werden sie deshalb genau einmal: ein zweiter
            // Durchlauf durch fetchArchivedPdfBytes hiesse, jedes PDF ein
            // zweites Mal aus dem Archiv zu holen — doppelte Ladezeit fuer
            // Bytes, die schon da sind.
            const pdfBytes: Array<{ fileName: string; bytes: Uint8Array }> = [];

            setProgress({ done: 0, total: entries.length });
            for (const [index, entry] of entries.entries()) {
                const invoice = byPath.get(entry.sourcePath);
                if (!invoice) continue;
                const bytes = await fetchArchivedPdfBytes(invoice);
                folder?.file(entry.fileName, bytes);
                pdfBytes.push({ fileName: entry.fileName, bytes });
                // Dieselbe GUID steht im Buchungsstapel als Beleglink. Laufen
                // die beiden auseinander, haengt kein Beleg mehr an seiner
                // Buchung — und genau dafuer ist das Manifest da.
                written.push({
                    guid: documentGuidForInvoice(invoice),
                    fileName: entry.fileName,
                });
                setProgress({ done: index + 1, total: entries.length });
            }

            // Ohne Beleg kein Manifest: eine leere document.xml im Ordner
            // wuerde nur die Frage aufwerfen, wo die Belege geblieben sind.
            //
            // Und ohne Beleg auch keine Beleg-ZIP: sie traegt denselben
            // Inhalt, aus derselben Quelle (written), und waere leer genauso
            // sinnlos.
            if (written.length > 0) {
                const documentXml = buildDocumentXml(written, generatedAt);
                folder?.file('document.xml', documentXml);

                // Die Belegverwaltung von DATEV importiert eine ZIP, keinen
                // Ordner. Genau daran hing der erste Produktivimport: die
                // GUIDs wurden nie registriert, und der Beleglink BEDI "<guid>"
                // im Buchungsstapel lief deshalb ins Leere. Der Referenzexport
                // der Kanzlei liefert beides — den Ordner zum Hineinsehen, die
                // gleichnamige ZIP zum Importieren.
                //
                // Entscheidend ist die Wurzel: document.xml und die PDFs liegen
                // OHNE Ordnerebene darin. Eine ZIP, die den Ordner noch einmal
                // enthaelt, nimmt DATEV nicht an.
                const belegZip = new JSZip();
                belegZip.file('document.xml', documentXml);
                for (const pdf of pdfBytes) belegZip.file(pdf.fileName, pdf.bytes);
                zip.file(
                    `${belegFolderName}.zip`,
                    await belegZip.generateAsync({ type: 'uint8array', compression: 'DEFLATE' }),
                );
            }

            // Die CSV entsteht ERST JETZT, aus derselben Menge wie das
            // Manifest: die guid eines Manifesteintrags IST die Rechnungs-id.
            // Vorher gebaut, muesste sie raten, welche Belege es ins Paket
            // schaffen — und Belege ohne PDF (siehe missingPdf) bekaemen einen
            // Beleglink auf ein Dokument, das nirgends liegt.
            const csv = await buildCsv(new Set(written.map((entry) => entry.guid)));
            zip.file(csv.fileName, csv.bytes);

            // Der Debitorenstamm — die "Uebersetzungsdatei", die den Konten des
            // Stapels ihre Beschriftung gibt. Er entsteht ERST NACH dem Stapel:
            // dieselbe Kontoableitung wirft bei einer unbrauchbaren
            // Kundennummer, und dann soll der Stapel abbrechen und nicht der
            // Stamm still eine Zeile verlieren.
            //
            // Name und Datumsteil folgen dem Referenzpaket der Kanzlei; das
            // Datum ist der Erzeugungstag, abgelesen von demselben
            // generatedAt wie das Manifest.
            const skippedDebtors: string[] = [];
            const debtorMaster = exportCustomersDatevExtf(
                debtorCustomersOf(preview.exportable),
                datevConfig,
                generatedAt,
                { onSkipped: (message) => skippedDebtors.push(message) },
            );
            // Windows-1252 wie der Buchungsstapel nebenan und wie die
            // Referenzdatei der Kanzlei. Als UTF-8 in die ZIP geschrieben
            // (so gibt die Funktion den Text heraus, mit BOM fuer Excel) kaeme
            // "Thoennessen" bei DATEV verstuemmelt an, ohne dass der Import
            // scheitert. Das BOM faellt dabei weg — die Referenz hat keines.
            zip.file(
                `EXTF_GP_Stamm_${formatYearMonthDay(isoDay(generatedAt), 'Erzeugungstag')}.csv`,
                toCp1252Bytes(debtorMaster.replace(/^\ufeff/, '')),
            );

            const bytes = await zip.generateAsync({ type: 'uint8array', compression: 'DEFLATE' });
            const fileName = csv.fileName.replace(/\.csv$/, '') + '_mit_Belegen.zip';
            await download(bytes, fileName, 'application/zip');
            const marked = await reportExport('zip', fileName);

            const pdfNote =
                missingPdf.length > 0
                    ? ` Zu ${missingPdf.length} Beleg(en) liegt kein PDF im Archiv: ${missingPdf.join(', ')}.`
                    : '';
            // Kann hier eigentlich nicht auftreten — der Buchungsstapel waere
            // an demselben Kunden schon vorher abgebrochen. Steht trotzdem da,
            // damit ein ausgelassenes Konto niemals still bleibt.
            const debtorNote =
                skippedDebtors.length > 0
                    ? ` ${skippedDebtors.length} Kunde(n) fehlen im Debitorenstamm: ${skippedDebtors.join(' ')}`
                    : '';
            if (marked.ok) setNotice(`${fileName} wurde heruntergeladen.${pdfNote}${debtorNote}`);
            else
                setError(
                    (marked.message ??
                        `${fileName} wurde heruntergeladen, der Exportvermerk konnte aber nicht gesetzt werden.`) +
                        pdfNote +
                        debtorNote,
                );
        } catch (e) {
            setError(errorText(e, 'Das Belegpaket konnte nicht erzeugt werden.'));
        } finally {
            setBusy(null);
        }
    };

    const handleGobd = async () => {
        setBusy('gobd');
        setError(null);
        setNotice(null);
        setGobd(null);
        try {
            setProgress({ done: 0, total: preview.exportable.length });
            const result = await buildGobdArchive(preview.exportable, {
                exportedBy,
                description: `Belegarchiv ${from} bis ${to}`,
                period: { from, to },
                onProgress: (done, total) => setProgress({ done, total }),
            });
            await download(result.bytes, result.fileName, 'application/zip');
            setGobd(result);
            // Bewusst KEIN onExported: ein Belegarchiv ist kein Buchungsexport.
            // Würde es exportedAt setzen, gälten die Belege fälschlich als an die
            // Kanzlei übergeben.
        } catch (e) {
            setError(errorText(e, 'Das GoBD-Archiv konnte nicht erzeugt werden.'));
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 animate-fade-in backdrop-blur-sm p-4">
            <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
                <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
                    <h2 className="flex items-center gap-2 font-semibold text-slate-800">
                        <Download className="h-5 w-5 text-slate-400" />
                        DATEV-/Simba-Export und GoBD-Archiv
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded p-1 text-slate-400 hover:bg-slate-100"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-auto">
                    {/* Der Hinweis steht vor allem anderen und ist nicht einklappbar. */}
                    <div className="border-b-4 border-amber-400 bg-amber-50 px-5 py-4">
                        <p className="flex items-center gap-2 text-sm font-bold text-amber-900">
                            <AlertTriangle className="h-5 w-5 shrink-0" />
                            Die Feldbelegung dieses Exports ist NICHT gegen die DATEV-Dokumentation
                            geprüft
                        </p>
                        <p className="mt-2 text-sm text-amber-900">
                            Kopfzeile und Spaltenliste des Buchungsstapels wurden ohne
                            Formatbeschreibung erstellt. Vor dem ersten Produktivexport müssen beide
                            mit dem Steuerberater abgestimmt und mit einem Probeimport überprüft
                            werden. Ein falsch belegtes Feld führt nicht zu einer Fehlermeldung — es
                            bucht still auf das falsche Konto und fällt erst beim Kontenabgleich
                            auf.
                        </p>
                        <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-amber-900">
                            {UNVERIFIED_DATEV_FIELDS.length} offene Punkte
                        </p>
                        <ol className="mt-1 list-decimal space-y-1 pl-5 text-xs text-amber-900">
                            {UNVERIFIED_DATEV_FIELDS.map((text) => (
                                <li key={text}>{text}</li>
                            ))}
                        </ol>
                        <label className="mt-3 flex items-start gap-2 text-sm font-medium text-amber-900">
                            <input
                                type="checkbox"
                                checked={formatAcknowledged}
                                onChange={(e) => setFormatAcknowledged(e.target.checked)}
                                className="mt-0.5"
                            />
                            <span>
                                Ich habe die offenen Punkte gelesen und kläre sie vor dem
                                produktiven Import mit dem Steuerberater.
                            </span>
                        </label>
                    </div>

                    {/* Deutlich genug, um neben dem gelben Formathinweis nicht
                        unterzugehen: hier steht auch die Warnung, dass der
                        Exportvermerk fehlt. */}
                    {error && (
                        <div className="flex items-start gap-2 border-b-4 border-red-400 bg-red-50 px-5 py-3 text-sm font-semibold text-red-800">
                            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}
                    {notice && (
                        <div className="flex items-start gap-2 border-b border-emerald-200 bg-emerald-50 px-5 py-2 text-sm text-emerald-800">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                            <span>{notice}</span>
                        </div>
                    )}
                    {/* Steht direkt unter der Fehlermeldung, die den Abbruch
                        nennt: dort erfaehrt der Anwender, WAS schiefging, hier,
                        was das fuer die Zahlen unter dieser Zeile bedeutet. */}
                    {markStateUnknown && (
                        <div className="flex items-start gap-2 border-b-4 border-amber-400 bg-amber-50 px-5 py-3 text-sm text-amber-900">
                            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                            <span>
                                <span className="font-semibold">
                                    Der Exportstand war nach dem Abbruch unsicher und wurde neu aus
                                    der Datenbank geladen.
                                </span>{' '}
                                Ein Teil der Belege kann bereits als exportiert vermerkt sein, und
                                die Datei ist heruntergeladen. Bitte unter „Schon exportiert“
                                prüfen, ob der Stapel in der Buchhaltung angekommen ist, bevor
                                erneut exportiert wird — ein zweiter Import derselben Buchungen
                                verdoppelt den Umsatz. Jeder weitere Export in diesem Fenster muss
                                deshalb gesondert bestätigt werden.
                            </span>
                        </div>
                    )}
                    {/* Der Ladezustand steht ueber den Zahlen und nicht neben
                        ihnen: "0 Belege" waehrend des Ladens laese sich als
                        Ergebnis lesen, und wer den Zeitraum daraufhin
                        aendert, sucht den Fehler an der falschen Stelle. */}
                    {invoices === null && !loadError && (
                        <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-5 py-2 text-sm text-slate-600">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Die Belege werden mit ihren Positionen geladen …
                        </div>
                    )}
                    {loadError && (
                        <div className="flex flex-wrap items-center gap-2 border-b-4 border-red-400 bg-red-50 px-5 py-3 text-sm font-semibold text-red-800">
                            <AlertTriangle className="h-5 w-5 shrink-0" />
                            <span className="flex-1">{loadError}</span>
                            <Button
                                variant="secondary"
                                onClick={() => setLoadAttempt((n) => n + 1)}
                            >
                                <RefreshCw className="h-4 w-4" /> Erneut versuchen
                            </Button>
                        </div>
                    )}
                    {!datevConfig && (
                        <div className="border-b border-red-200 bg-red-50 px-5 py-2 text-sm text-red-700">
                            Es ist keine DATEV-Konfiguration hinterlegt. Beraternummer,
                            Mandantennummer und Erlöskonten sind für den Export zwingend.
                        </div>
                    )}

                    <div className="space-y-4 p-5">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <label className="block text-sm">
                                <span className="mb-1 block text-slate-600">Belegdatum von</span>
                                <input
                                    type="date"
                                    value={from}
                                    onChange={(e) => setFrom(e.target.value)}
                                    className="w-full rounded border border-slate-300 px-2 py-1.5"
                                />
                            </label>
                            <label className="block text-sm">
                                <span className="mb-1 block text-slate-600">Belegdatum bis</span>
                                <input
                                    type="date"
                                    value={to}
                                    onChange={(e) => setTo(e.target.value)}
                                    className="w-full rounded border border-slate-300 px-2 py-1.5"
                                />
                            </label>
                        </div>
                        {!periodValid && (
                            <p className="-mt-2 text-sm text-red-700">
                                Bitte einen Zeitraum wählen, dessen Ende nicht vor dem Beginn liegt.
                            </p>
                        )}

                        <div className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:grid-cols-4">
                            <div>
                                <p className="text-xs uppercase text-slate-500">Belege</p>
                                <p className="text-lg font-semibold text-slate-800">
                                    {preview.exportable.length}
                                </p>
                                <p className="text-xs text-slate-500">
                                    {preview.bookingRowsExact
                                        ? `${preview.bookingRows} Buchungszeilen`
                                        : `bis zu ${preview.bookingRows} Buchungszeilen`}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs uppercase text-slate-500">Netto gefordert</p>
                                <p className="text-lg font-semibold tabular-nums text-slate-800">
                                    {formatCents(preview.netCents)}
                                </p>
                                {preview.hasDeductions && (
                                    <p className="text-xs tabular-nums text-slate-500">
                                        Leistung {formatCents(preview.totalNetCents)} − Abzug{' '}
                                        {formatCents(preview.deductedNetCents)}
                                    </p>
                                )}
                            </div>
                            <div>
                                <p className="text-xs uppercase text-slate-500">Brutto gefordert</p>
                                <p className="text-lg font-semibold tabular-nums text-slate-800">
                                    {formatCents(preview.grossCents)}
                                </p>
                                {preview.hasDeductions && (
                                    <p className="text-xs tabular-nums text-slate-500">
                                        Leistung {formatCents(preview.totalGrossCents)} − Abzug{' '}
                                        {formatCents(preview.deductedGrossCents)}
                                    </p>
                                )}
                            </div>
                            <div>
                                <p className="text-xs uppercase text-slate-500">Schon exportiert</p>
                                <p
                                    className={`text-lg font-semibold ${
                                        preview.alreadyExported.length > 0
                                            ? 'text-amber-700'
                                            : 'text-slate-800'
                                    }`}
                                >
                                    {preview.alreadyExported.length}
                                </p>
                            </div>
                        </div>

                        {/* Der Satz steht nur da, wo er etwas erklärt. Ohne
                            Abzüge sind gefordert und geleistet dasselbe, und
                            ein Hinweis darauf wäre nur ein Satz mehr. */}
                        {preview.hasDeductions && (
                            <p className="-mt-2 text-xs text-slate-600">
                                Gezeigt wird, was diese Belege tatsächlich fordern — die
                                Gesamtleistung abzüglich der darin abgezogenen Abschläge und
                                Vorrechnungen. Auf dem Beleg selbst steht die Gesamtleistung
                                (§ 14 UStG); gebucht wird der Zuwachs, denn das Abgezogene ist
                                bereits gebucht.
                            </p>
                        )}

                        {preview.monthly.length > 1 && (
                            <div className="rounded-lg border border-slate-200 p-4">
                                <p className="text-sm font-semibold text-slate-800">
                                    Summen je Monat
                                </p>
                                <p className="mt-1 text-xs text-slate-600">
                                    Maßgeblich ist das Belegdatum — es bestimmt die
                                    Buchungsperiode in der Buchhaltung.
                                </p>
                                <table className="mt-2 w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-xs uppercase text-slate-500">
                                            <th className="py-1 font-medium">Monat</th>
                                            <th className="py-1 text-right font-medium">Belege</th>
                                            {preview.hasDeductions && (
                                                <th className="py-1 text-right font-medium">
                                                    Abzug
                                                </th>
                                            )}
                                            <th className="py-1 text-right font-medium">Netto</th>
                                            <th className="py-1 text-right font-medium">Brutto</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {preview.monthly.map((row) => (
                                            <tr key={row.month} className="text-slate-700">
                                                <td className="py-1">
                                                    {new Date(
                                                        `${row.month}-01T00:00:00`,
                                                    ).toLocaleDateString('de-DE', {
                                                        month: 'long',
                                                        year: 'numeric',
                                                    })}
                                                </td>
                                                <td className="py-1 text-right tabular-nums">
                                                    {row.count}
                                                </td>
                                                {preview.hasDeductions && (
                                                    <td className="py-1 text-right tabular-nums text-slate-500">
                                                        {row.deductedGrossCents === 0
                                                            ? '—'
                                                            : `− ${formatCents(row.deductedGrossCents)}`}
                                                    </td>
                                                )}
                                                <td className="py-1 text-right tabular-nums">
                                                    {formatCents(row.netCents)}
                                                </td>
                                                <td className="py-1 text-right tabular-nums">
                                                    {formatCents(row.grossCents)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Auch ohne einen einzigen vermerkten Beleg zu zeigen,
                            sobald der Exportstand unsicher ist: die Bestaetigung
                            ist dann verlangt (reExportBlocked), und ohne dieses
                            Feld gaebe es keinen Weg, sie zu geben. */}
                        {(preview.alreadyExported.length > 0 || markStateUnknown) && (
                            <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4">
                                <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                                    <AlertTriangle className="h-4 w-4" />
                                    {preview.alreadyExported.length > 0
                                        ? `${preview.alreadyExported.length} Beleg(e) wurden bereits exportiert`
                                        : 'Der Exportstand dieses Zeitraums ist unsicher'}
                                </p>
                                <p className="mt-1 text-sm text-amber-900">
                                    Werden sie ein zweites Mal in die Buchhaltung eingelesen,
                                    entsteht der Umsatz doppelt. Sinnvoll ist das nur, wenn der
                                    erste Stapel nachweislich nicht angekommen oder verworfen
                                    worden ist.
                                </p>
                                <ul className="mt-2 space-y-0.5 text-xs text-amber-900">
                                    {preview.alreadyExported.map((invoice) => (
                                        <li key={invoice.id}>
                                            <span className="font-medium">
                                                {invoice.invoiceNumber}
                                            </span>{' '}
                                            — exportiert am{' '}
                                            {new Date(invoice.exportedAt as string).toLocaleString(
                                                'de-DE',
                                            )}
                                        </li>
                                    ))}
                                </ul>
                                <label className="mt-3 flex items-start gap-2 text-sm font-medium text-amber-900">
                                    <input
                                        type="checkbox"
                                        checked={reExportAcknowledged}
                                        onChange={(e) => setReExportAcknowledged(e.target.checked)}
                                        className="mt-0.5"
                                    />
                                    <span>
                                        Diese Belege sollen erneut exportiert werden. Der
                                        Doppelbuchung wird in der Buchhaltung vorgebeugt.
                                    </span>
                                </label>
                            </div>
                        )}

                        {/* Nur solange die Feststellung fehlt: ist sie
                            getroffen, bucht der Export die Alt-Abschlaege
                            danach, und der Hinweis haette nichts mehr zu
                            sagen. */}
                        {preview.withExternalDeductions.length > 0 &&
                            !datevConfig?.externalAdvanceBooking && (
                                <div className="rounded-lg border-2 border-red-300 bg-red-50 p-4">
                                    <p className="flex items-center gap-2 text-sm font-semibold text-red-800">
                                        <AlertTriangle className="h-4 w-4" />
                                        {preview.withExternalDeductions.length} Beleg(e) ziehen
                                        einen externen Alt-Abschlag ab — der Export bricht daran ab
                                    </p>
                                    <p className="mt-1 text-sm text-red-800">
                                        Externe Alt-Abschläge stammen aus dem Vorsystem und stehen
                                        nicht in der Belegverwaltung. Ob sie dort als erhaltene
                                        Anzahlung oder als Erlös gebucht wurden, weiß die Anwendung
                                        nicht — und davon hängt ab, wie der Abzug zu buchen ist.
                                        Geraten wird das nicht: die falsche Annahme bucht Umsatz
                                        doppelt oder gar nicht, und das fällt erst beim
                                        Kontenabgleich auf. Die Kanzlei beantwortet es einmal;
                                        einzutragen ist es in der Verwaltung unter Rechnungen →
                                        Steuerberater-Export (DATEV / Simba) als „Alt-Abschläge im
                                        Vorsystem gebucht als“.
                                    </p>
                                    <ul className="mt-2 space-y-0.5 text-xs text-red-800">
                                        {preview.withExternalDeductions.map(
                                            ({ invoice, sources }) => (
                                                <li key={invoice.id}>
                                                    <span className="font-medium">
                                                        {invoice.invoiceNumber}
                                                    </span>{' '}
                                                    — zieht ab: {sources.join(', ')}
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                </div>
                            )}

                        {preview.blocked.length > 0 && (
                            <div className="rounded-lg border border-slate-200 p-4">
                                <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                                    {preview.blocked.length} Beleg(e) im Zeitraum gehen NICHT mit
                                </p>
                                <p className="mt-1 text-xs text-slate-600">
                                    Sie werden hier aufgeführt statt stillschweigend übersprungen —
                                    sonst fiele ihr Fehlen erst beim Kontenabgleich auf.
                                </p>
                                <ul className="mt-2 divide-y divide-slate-100">
                                    {preview.blocked.map(({ invoice, reason }) => (
                                        <li
                                            key={invoice.id}
                                            className="flex flex-wrap items-center gap-2 py-1.5 text-xs"
                                        >
                                            <InvoiceStatusBadge
                                                invoice={invoice}
                                                today={to}
                                                showDays={false}
                                            />
                                            <span className="font-medium text-slate-700">
                                                {invoice.invoiceNumber ?? 'ohne Nummer'}
                                            </span>
                                            <span className="text-slate-500">
                                                {invoice.invoiceDate}
                                            </span>
                                            <span className="text-slate-500">
                                                {invoice.customerSnapshot?.name ??
                                                    invoice.customerName ??
                                                    ''}
                                            </span>
                                            <span className="tabular-nums text-slate-500">
                                                {formatCents(invoice.totals.grossTotalCents)}
                                            </span>
                                            <span className="basis-full text-slate-600">
                                                {reason}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {preview.exportable.length > 0 && (
                            <div className="rounded-lg border border-slate-200">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                                        <tr>
                                            <th className="px-3 py-2">Nummer</th>
                                            <th className="px-3 py-2">Datum</th>
                                            <th className="px-3 py-2">Kunde</th>
                                            {preview.hasDeductions && (
                                                <th className="px-3 py-2 text-right">Abzug</th>
                                            )}
                                            <th className="px-3 py-2 text-right">Netto</th>
                                            <th className="px-3 py-2 text-right">Brutto</th>
                                            <th className="px-3 py-2">Export</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {preview.exportable.map((invoice) => (
                                            <tr
                                                key={invoice.id}
                                                className={invoice.exportedAt ? 'bg-amber-50' : ''}
                                            >
                                                <td className="px-3 py-2 font-medium text-slate-800">
                                                    {invoice.invoiceNumber}
                                                </td>
                                                <td className="px-3 py-2 text-slate-600">
                                                    {invoice.invoiceDate}
                                                </td>
                                                <td className="px-3 py-2 text-slate-600">
                                                    {invoice.customerSnapshot?.name ??
                                                        invoice.customerName ??
                                                        ''}
                                                </td>
                                                {preview.hasDeductions && (
                                                    <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                                                        {invoice.totals.prepaidCents === 0
                                                            ? '—'
                                                            : `− ${formatCents(invoice.totals.prepaidCents)}`}
                                                    </td>
                                                )}
                                                {/* Beide Spalten zeigen das
                                                    Geforderte, wie die Kacheln
                                                    darüber — nicht die
                                                    Gesamtleistung des Belegs. */}
                                                <td className="px-3 py-2 text-right tabular-nums">
                                                    {formatCents(
                                                        invoice.totals.netTotalCents -
                                                            deductedNetOf(invoice),
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 text-right font-medium tabular-nums">
                                                    {formatCents(invoice.totals.payableCents)}
                                                </td>
                                                <td className="px-3 py-2 text-xs">
                                                    {invoice.exportedAt ? (
                                                        <span className="rounded bg-amber-200 px-1.5 py-0.5 font-medium text-amber-900">
                                                            bereits exportiert
                                                        </span>
                                                    ) : (
                                                        <span className="text-slate-400">neu</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Erst aussagen, wenn die Belege da sind: solange sie
                            laden, waere "kein Beleg" schlicht falsch. */}
                        {invoices !== null && preview.inPeriod.length === 0 && (
                            <p className="text-sm text-slate-600">
                                Im gewählten Zeitraum liegt kein Beleg.
                            </p>
                        )}

                        <div className="rounded-lg border border-slate-200 p-4">
                            <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                                <Archive className="h-4 w-4 text-slate-400" />
                                GoBD-Belegarchiv
                            </p>
                            <p className="mt-1 text-xs text-slate-600">
                                Ein ZIP für die Betriebsprüfung: alle archivierten PDFs, eine
                                index.csv und eine audit.json mit den Belegmetadaten. Beim Packen
                                wird jedes PDF neu gehasht und gegen den beim Ausstellen
                                gespeicherten Wert gehalten; Abweichungen stehen in einer eigenen
                                Spalte und brechen den Lauf nicht ab. Das Archiv ist kein
                                Buchungsexport und markiert keinen Beleg als exportiert.
                            </p>
                            <div className="mt-3">
                                <Button
                                    variant="secondary"
                                    onClick={() => void handleGobd()}
                                    disabled={busy !== null || preview.exportable.length === 0}
                                >
                                    {busy === 'gobd' ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            {progress.done} von {progress.total} gepackt
                                        </>
                                    ) : (
                                        'GoBD-Archiv erzeugen'
                                    )}
                                </Button>
                            </div>

                            {gobd && (
                                <div className="mt-3 space-y-2 text-xs">
                                    <p className="text-slate-700">
                                        {gobd.fileName} — {gobd.entries.length} Beleg(e) geprüft,{' '}
                                        {gobd.mismatched.length} Abweichung(en),{' '}
                                        {gobd.incomplete.length} ohne PDF.
                                    </p>
                                    {gobd.warnings.map((warning) => (
                                        <p key={warning} className="text-amber-700">
                                            {warning}
                                        </p>
                                    ))}
                                    {gobd.mismatched.length > 0 && (
                                        <div className="rounded border-2 border-red-300 bg-red-50 p-3 text-red-800">
                                            <p className="font-semibold">
                                                Die Prüfsumme stimmt bei{' '}
                                                {gobd.mismatched.length} Beleg(en) nicht mehr mit
                                                dem beim Ausstellen festgehaltenen Wert überein.
                                            </p>
                                            <ul className="mt-1 space-y-0.5">
                                                {gobd.mismatched.map((entry) => (
                                                    <li key={entry.invoiceId}>
                                                        {entry.invoiceNumber ?? entry.invoiceId}:{' '}
                                                        erwartet {entry.expectedSha256}, gefunden{' '}
                                                        {entry.actualSha256}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {gobd.incomplete.length > 0 && (
                                        <ul className="space-y-0.5 text-slate-600">
                                            {gobd.incomplete.map((entry) => (
                                                <li key={entry.invoiceId}>
                                                    {entry.invoiceNumber ?? entry.invoiceId}:{' '}
                                                    {entry.message}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
                    {!formatAcknowledged && (
                        <span className="mr-auto text-xs text-amber-800">
                            Der Hinweis zur ungeprüften Feldbelegung ist noch nicht bestätigt.
                        </span>
                    )}
                    {formatAcknowledged && reExportBlocked && (
                        <span className="mr-auto text-xs text-amber-800">
                            {preview.alreadyExported.length > 0
                                ? 'Der erneute Export bereits exportierter Belege ist noch nicht bestätigt.'
                                : 'Der erneute Export bei unsicherem Exportstand ist noch nicht bestätigt.'}
                        </span>
                    )}
                    <Button variant="secondary" onClick={onClose} disabled={busy !== null}>
                        Schließen
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={() => void handleZip()}
                        disabled={!canExport}
                        icon={<Archive className="h-4 w-4" />}
                    >
                        {busy === 'zip' ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                {progress.done} von {progress.total} Belegen
                            </>
                        ) : (
                            'CSV + Belege als ZIP'
                        )}
                    </Button>
                    <Button
                        onClick={() => void handleCsv()}
                        disabled={!canExport}
                        icon={<FileText className="h-4 w-4" />}
                    >
                        {busy === 'csv' ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Wird erzeugt …
                            </>
                        ) : (
                            'Buchungsstapel herunterladen'
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};
